import { BREAKPOINTS } from '@skyscanner-internal/backpack-web/bpk-component-breakpoint';
import BpkText from '@skyscanner-internal/backpack-web/bpk-component-text';
import { useSelector } from 'react-redux';

import './MonthViewChart.scss';

import getFeatureDecision from '../../../../config';
import { ENABLE_DEPARTURE_TIMES } from '../../../../config/constants';
import { useLocalisation } from '../../../../services/contextAdaptor/LocalisationContext';
import { useMediaQuery } from '../../helpers/hooks/useMediaQuery';
import { useTranslation } from '../../helpers/hooks/useTranslation';
import { isReturn } from '../../selectors';
import { ScaledChartWithLoading } from '../BarChart/ScaledChartWithLoading';
import { DepartureTimesFilter } from '../DepartureTimesFilter/DepartureTimesFilter';
import MonthSelector from '../MonthSelector/MonthSelector';

import type { Direction } from '../../selectors/types';

type MonthViewChartProps = {
  direction: Direction;
};

const MonthViewChart = ({ direction }: MonthViewChartProps) => {
  const { daysOfWeek, formatCurrency, formatDateFull } = useLocalisation();
  const isReturnTrip = useSelector(isReturn);
  const departureTimesVariant = getFeatureDecision(ENABLE_DEPARTURE_TIMES, 'A');
  const enableDepartureTimes = departureTimesVariant !== 'A' && !isReturnTrip;
  const singleSelect = departureTimesVariant === 'B';
  const stackedFilter = enableDepartureTimes && !singleSelect;
  const isMobile = useMediaQuery(BREAKPOINTS.MOBILE);
  const translate = useTranslation();
  const chartLabel = `${translate('page_header_title_label')}: ${translate(
    direction === 'outbound' ? 'outbound_month_label' : 'inbound_month_label',
  )} ${translate('chart_card_label')}`;

  const getDayOfWeekName = (dayOfWeek: number | string) => {
    const dayOfWeekIndex =
      typeof dayOfWeek === 'string' ? parseInt(dayOfWeek, 10) : dayOfWeek;
    const daysOfWeekSorted = [...daysOfWeek].sort((a, b) =>
      a.index > b.index ? 1 : -1,
    );
    return daysOfWeekSorted[dayOfWeekIndex].nameNarrow;
  };

  return (
    <div className="month-view-chart">
      <div
        className={`month-view-chart__selectors${stackedFilter ? ' month-view-chart__selectors--stacked' : ''}`}
      >
        <div>
          <MonthSelector direction={direction} id={direction} />
        </div>
        {enableDepartureTimes && (
          <div className="month-view-chart__departure-time-filters">
            {(isMobile || stackedFilter) && (
              <BpkText>{translate('mvp_departure_time')}</BpkText>
            )}
            <DepartureTimesFilter />
          </div>
        )}
      </div>
      <section
        className="month-view-chart__chart-area"
        data-percy-id="chart-area"
        aria-label={chartLabel}
      >
        <ScaledChartWithLoading
          direction={direction}
          currencyFormatter={formatCurrency}
          dateFormatter={formatDateFull}
          dayOfWeekNameProvider={getDayOfWeekName}
        />
      </section>
    </div>
  );
};

export default MonthViewChart;
